import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import { Plus, Trash2, SquarePen, Upload } from 'lucide-react';
import { getFacilities, createFacility, updateFacility, deleteFacility } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';

const StatusBadge = ({ isActive }) => (
    <Box sx={{
        display: 'inline-block',
        px: 1.5, py: 0.5,
        borderRadius: '4px',
        bgcolor: isActive ? '#E8F5E9' : '#FEECEB',
        color: isActive ? '#2E7D32' : '#F44336',
        fontSize: '0.7rem',
        fontWeight: 600
    }}>
        {isActive ? "Active" : "Inactive"}
    </Box>
);

const GetFacilities = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [facilities, setFacilities] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalError, setModalError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        image: null,
        displayOrder: '1',
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchFacilities = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getFacilities();
            if (response && response.success) {
                setFacilities(response.data);
            } else {
                setError("Failed to load facilities.");
            }
        } catch (err) {
            setError("Failed to fetch facilities. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFacilities();
    }, [fetchFacilities]);

    const handleOpenModal = (item = null) => {
        setModalError(null);
        setValidationErrors({});
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                name: item.name || '',
                image: null,
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                isActive: item.isActive ?? true
            });
            setImagePreview(item.image || null);
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                name: '',
                image: null,
                displayOrder: '1',
                isActive: true
            });
            setImagePreview(null);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setModalError(null);
        setFormData({ name: '', image: null, displayOrder: '1', isActive: true });
        setImagePreview(null);
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        if (name === 'displayOrder') {
            if (value !== '') {
                const num = parseInt(value, 10);
                if (num > 999) finalValue = 999;
                else if (num < 1) finalValue = 1;
                else finalValue = num;
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSwitchChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setValidationErrors(prev => ({
                    ...prev,
                    image: 'Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed'
                }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = '';
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                setValidationErrors(prev => ({
                    ...prev,
                    image: 'Image size must be less than 5MB'
                }));
                showSnackbar("Image size must be less than 5MB", "error");
                e.target.value = '';
                return;
            }

            setFormData(prev => ({
                ...prev,
                image: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);

            // Clear image-specific error
            if (validationErrors.image) {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.image;
                    return newErrors;
                });
            }
        }
    };

    const handleSubmit = async () => {
        setModalError(null);
        if (!formData.name) {
            setModalError("Name is required");
            return;
        }

        if (formData.image && formData.image instanceof File && !formData.image.type.startsWith('image/')) {
            setModalError("Please upload a valid image file");
            return;
        }

        setSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('isActive', formData.isActive);
            if (formData.displayOrder !== '') {
                formDataToSend.append('displayOrder', formData.displayOrder);
            }

            if (formData.image && formData.image instanceof File) {
                formDataToSend.append('facilites-images', formData.image);
            }

            if (isEdit) {
                await updateFacility(currentItem.id, formDataToSend);
            } else {
                if (!formData.image) {
                    setModalError("Image is required for creating a facility");
                    setSubmitting(false);
                    return;
                }
                await createFacility(formDataToSend);
            }

            handleCloseModal();
            fetchFacilities();
            showSnackbar(`Facility ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            const serverError = err.response?.data;
            const message = serverError?.errors?.[0] || serverError?.details?.[0]?.message || serverError?.message || serverError?.error?.message || `Failed to ${isEdit ? 'update' : 'create'} facility`;

            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = (detail.field === 'general' || detail.field === 'name') ? 'name' : (detail.field === 'facilites-images' || detail.field === 'file' ? 'image' : detail.field);
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else if (message.toLowerCase().includes('image') || message.toLowerCase().includes('file')) {
                setValidationErrors(prev => ({ ...prev, image: message }));
            } else {
                setValidationErrors({ name: message });
            }
            setModalError(message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteFacility(selectedId);
                if (response && response.success) {
                    showSnackbar("Facility deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchFacilities();
                } else {
                    const serverError = response;
                    const errorMsg = Array.isArray(serverError?.errors)
                        ? serverError.errors.join(", ")
                        : (serverError?.details?.[0]?.message || serverError?.message || "Failed to delete facility");
                    setDeleteError(errorMsg);
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const serverError = err.response?.data;
                const errorMessage = Array.isArray(serverError?.errors)
                    ? serverError.errors.join(", ")
                    : (serverError?.details?.[0]?.message || serverError?.message || err.message || "Failed to delete facility");
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Facilities
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained"
                            startIcon={<Plus size={18} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                color: '#fff',
                                borderRadius: '6px',
                                height: '40px',
                                textTransform: 'none',
                                fontSize: '0.85rem'
                            }}
                        >
                            Add Facility
                        </Button>
                    </PermissionWrapper>
                </Box>

                {!openModal && error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1050 }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '70px' }}>Image</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Display Order</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'right' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                            <CircularProgress sx={{ color: '#FFBD65' }} size={24} />
                                        </TableCell>
                                    </TableRow>
                                ) : facilities.length > 0 ? (
                                    facilities.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                {item.image && (
                                                    <img
                                                        src={item.image}
                                                        alt={item.name}
                                                        style={{ width: 44, height: 34, objectFit: 'cover', borderRadius: '4px', border: '1px solid #EEE' }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', fontWeight: 500, color: '#333' }}>{item.name}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666' }}>{item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : '—'}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                                <StatusBadge isActive={item.isActive} />
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(item.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5 }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                            No facilities found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>
                        {isEdit ? 'Edit Facility' : 'Add Facility'}
                    </DialogTitle>
                    <DialogContent>
                        {openModal && modalError && <Alert severity="error" sx={{ mb: 2, borderRadius: '6px' }} onClose={() => setModalError(null)}>{modalError}</Alert>}
                        <Stack spacing={1.8} sx={{ mt: 2 }}>
                            <TextField
                                fullWidth
                                label="Name"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. Swimming Pool"
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Display Order"
                                name="displayOrder"
                                type="number"
                                value={formData.displayOrder}
                                onChange={handleInputChange}
                                placeholder="e.g. 1"
                                inputProps={{ min: 1, max: 999 }}
                                error={!!validationErrors.displayOrder}
                                helperText={validationErrors.displayOrder || "Optional order number (1-999)"}
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.85rem',
                                        '& fieldset': { borderColor: '#E0E0E0' },
                                        '&:hover fieldset': { borderColor: '#BDBDBD' },
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    }
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleSwitchChange}
                                        name="isActive"
                                        color="primary"
                                    />
                                }
                                label={<Typography sx={{ fontSize: '0.85rem', color: '#444' }}>Active</Typography>}
                            />

                            <Box>
                                <Typography sx={{ mb: 1, color: '#444', fontSize: '0.85rem', fontWeight: 550 }}>
                                    Image {isEdit ? '(Leave empty to keep current)' : '(Required)'}
                                </Typography>
                                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                    component="label"
                                    variant="outlined"
                                    startIcon={<Upload size={16} />}
                                    sx={{
                                        borderColor: '#E8E8E8',
                                        color: '#666',
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        height: '40px',
                                        fontSize: '0.85rem',
                                        '&:hover': { borderColor: '#BDBDBD', bgcolor: '#fafafa' }
                                    }}
                                >
                                    Choose Image
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml"
                                        onChange={handleImageChange}
                                    />
                                </Button>
                                {validationErrors.image && (
                                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        {validationErrors.image}
                                    </Typography>
                                )}
                                {formData.image && formData.image instanceof File && (
                                    <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                                        {formData.image.name}
                                    </Typography>
                                )}

                                {(imagePreview || (formData.image && formData.image instanceof File)) && (
                                    <Box sx={{ mt: 2 }}>
                                        <img
                                            src={imagePreview || (formData.image instanceof File ? URL.createObjectURL(formData.image) : '')}
                                            alt="Preview"
                                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '6px', border: '1px solid #E0E0E0' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Facility"
                    description="Are you sure you want to delete this facility? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default GetFacilities;

